import { ownedByLabel } from '@lib/utils/libraryOwnership';
import { formatDurationToString, formatMbToString, pluralize } from '@lib/utils/units';
import { LibraryStore } from '@stores/Library/Library';

export interface FormattedLibraryStats {
  /** e.g. "24 media" — always "media", singular or plural (see words.md). */
  count: string | null;
  /** e.g. "3.4 GB" */
  size: string | null;
  /** e.g. "6h 12m" */
  duration: string | null;
}

/**
 * Formats a library's headline stats for display.
 *
 * Each part is `null` when the backend hasn't supplied it — `stats` arrives
 * inline on `GET /library`, but a `LibraryStore` can exist before that lands, so
 * a missing value must render as "nothing" rather than "0".
 *
 * Callers compose the parts themselves rather than receiving a joined string:
 * the Libraries list shows them as separate columns on desktop and dot-separated
 * on mobile, and the search-bar switcher uses a different order again.
 *
 * Library sizes are in **MB** (not bytes), hence `formatMbToString`. Don't
 * hand-roll the conversion — media sizes elsewhere in the codebase disagree on
 * whether a MB is 1000² or 1024², and library stats stay out of that.
 */
export function formatLibraryStats(library: LibraryStore): FormattedLibraryStats {
  const stats = library.stats;
  return {
    count: stats?.count !== undefined ? pluralize(stats.count, 'media') : null,
    size: stats?.mediaSize !== undefined ? formatMbToString(stats.mediaSize) : null,
    duration:
      stats?.mediaDuration !== undefined ? formatDurationToString(stats.mediaDuration) : null,
  };
}

/** Joins the supplied stat parts, dropping any that are unknown. */
export function joinLibraryStats(parts: (string | null)[], separator = ' · '): string {
  return parts.filter(Boolean).join(separator);
}

/**
 * What a library card says under its name: the media count and top-level folder count (drawn as
 * glyphs by the card), and — only when somebody else owns the library — who that is, on a line
 * of its own beneath them.
 *
 * Two lines rather than one joined string because they are not the same kind of fact. The counts
 * describe the library; the owner qualifies your relationship to it, and dot-separating them
 * put a rare, important line in the same breath as a stat you read past. Separated, the card can
 * also render the owner a size smaller and dimmer, which is what makes it discreet rather than
 * merely short.
 *
 * Shared because four places render that card: the Libraries grid, the copy that floats above
 * the mobile context drawer when you long-press a row, and the live previews in the desktop and
 * mobile edit modals. All four wrote out `${count} media` independently, so the three previews
 * silently kept the bare count while the grid gained an owner line — previews of a card that no
 * longer looked like that.
 *
 * Every part is a primitive, and must stay that way: `LibraryCard` is memoised on prop identity,
 * so callers destructure these into primitive props rather than handing the object (or a
 * ReactNode) down — otherwise every card in the grid re-renders on every pass. See
 * `LibraryGridCard`.
 */
export function formatLibraryCardMeta(library: LibraryStore): {
  /**
   * Null while the stats haven't landed. The folder count waits for it too: the folders arrive
   * with the library, and shown alone first they would be pushed aside when the count lands.
   */
  mediaCount: number | null;
  /** Folders at the library's top level. */
  folderCount: number;
  /** e.g. "Owned by Sam". Null for your own libraries and for an unnamed owner. */
  owner: string | null;
} {
  const ownerName = library.otherOwnerName;
  return {
    mediaCount: library.stats?.count ?? null,
    folderCount: library.topLevelFolderCount,
    owner: ownerName ? ownedByLabel(ownerName) : null,
  };
}
